import { useState, useEffect } from "react";
import logo from "./images/techvanto-logo.png";

const LOGO_SRC = logo;

// ── Universal Nav Data ─────────────────────────────────────────────────────────
const NAV_LINKS = [
  {
    label: "Services",
    dropdown: [
      { name: "Web Development", link: "/web-development" },
      { name: "Mobile App Development", link: "/#services" },
      { name: "AI & ML Solutions", link: "/#services" },
      { name: "Cloud DevOps", link: "/#services" },
      { name: "UI/UX Design", link: "/#services" },
      { name: "E-Commerce Solutions", link: "/#services" },
    ],
  },
  {
    label: "Solutions",
    dropdown: [
      { name: "CRM Solution", link: "/#services" }, 
      { name: "ERP Solution", link: "/#services" }, 
      { name: "HRM Solution", link: "/#services" },
      { name: "LMS Solution", link: "/#services" }, 
      { name: "POS Solution", link: "/#services" }, 
      { name: "SaaS Platform", link: "/#services" },
    ],
  },
  {
    label: "Industries",
    dropdown: [
      { name: "Healthcare", link: "/#services" }, 
      { name: "FinTech", link: "/#services" }, 
      { name: "EdTech", link: "/#services" },
      { name: "Retail & E-Commerce", link: "/#services" }, 
      { name: "Real Estate", link: "/#services" }, 
      { name: "Logistics", link: "/#services" },
    ],
  },
  { label: "Portfolio", dropdown: null, link: "/portfolio" },
  { label: "About", dropdown: null, link: "/#about" },
  { label: "Blog", dropdown: null, link: "/blogs" },
];
const ALL_BLOG_POSTS = [
  {
    category: "AI & ML",
    title: "How Generative AI Is Redefining Enterprise Software in 2025",
    excerpt: "From intelligent document processing to code generation, GenAI is moving from hype to hard ROI.",
    img: "https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=600&h=400&fit=crop&auto=format",
    date: "Apr 10, 2025",
    readTime: "5 min",
    link: "/blog/generative-ai-enterprise", // Corrected dynamic link
  },
  {
    category: "Mobile",
    title: "Flutter vs React Native in 2025: Which to Choose?",
    excerpt: "A pragmatic side-by-side comparison covering performance, ecosystem maturity, and team considerations.",
    img: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&h=400&fit=crop&auto=format",
    date: "Mar 28, 2025",
    readTime: "7 min",
    link: "/blog/flutter-vs-react-native", // Corrected dynamic link
  },
  {
    category: "Cloud",
    title: "Kubernetes Cost Optimization: 8 Strategies That Actually Work",
    excerpt: "Real-world tactics to slash cloud spend without sacrificing reliability or developer velocity.",
    img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop&auto=format",
    date: "Mar 15, 2025",
    readTime: "6 min",
    link: "/blog/kubernetes-cost-optimization", // Corrected dynamic link
  },
  {
    category: "Web Dev",
    title: "Why We Migrated to Next.js 15 for Enterprise Applications",
    excerpt: "Exploring the performance benefits, app router architecture, and caching strategies that drove our decision.",
    img: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=600&h=400&fit=crop&auto=format",
    date: "Feb 22, 2025",
    readTime: "8 min",
    link: "/blog/nextjs-enterprise-migration" // Linking to the AI post for demo purposes
  },
  {
    category: "UI/UX",
    title: "Designing for Accessibility: Beyond Color Contrast",
    excerpt: "True accessibility means rethinking navigation, screen reader flow, and cognitive load for all users.",
    img: "https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?w=600&h=400&fit=crop&auto=format",
    date: "Feb 05, 2025",
    readTime: "4 min",
    link: "/blog/designing-for-accessibility", // <--- NEW CORRECT LINK
  },
  {
    category: "Security",
    title: "Zero Trust Architecture: A Practical Implementation Guide",
    excerpt: "How to move from perimeter-based security to a modern identity-first security model.",
    img: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600&h=400&fit=crop&auto=format",
    date: "Jan 18, 2025",
    readTime: "10 min",
    link: "/blog/zero-trust-architecture-guide", // <--- NEW CORRECT LINK
  }
];

// ── Helper Components ─────────────────────────────────────────────────────────

function Badge({ children, color = "#3B82F6" }) {
  return (
    <span style={{
      display: "inline-block", padding: "4px 12px",
      borderRadius: 99, fontSize: 11.5, fontWeight: 700,
      background: color + "18", color, border: `1px solid ${color}33`,
      letterSpacing: ".04em", textTransform: "uppercase",
    }}>{children}</span>
  );
}

// ── Navbar ────────────────────────────────────────────────────────────────────

// ── Universal Navbar ─────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Smart logic to check which page we are currently on
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.9)",
      backdropFilter: "blur(16px)",
      borderBottom: scrolled ? "1px solid #E5E7EB" : "1px solid transparent",
      boxShadow: scrolled ? "0 2px 24px rgba(0,0,0,0.08)" : "none",
      transition: "all .3s ease",
    }}>
      <div style={{
        background: "linear-gradient(90deg,#1E3A8A,#3B82F6)",
        color: "#fff", fontSize: 12, padding: "6px 40px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <span>📞 +91 96462 06032</span>
          <span>✉️ info@techvanto.com</span>
        </div>
        <span className="topbar-right" style={{ opacity: .85 }}>🌏 Trusted by 1500+ clients across 20+ countries</span>
      </div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px", height: 68, maxWidth: 1280, margin: "0 auto",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
          <img src={LOGO_SRC} alt="Techvanto" style={{ height: 150, width: "auto", objectFit: "contain", display: "block" }} />
        </a>
        <div className="nav-links" style={{ alignItems: "center", gap: 4 }}>
          {NAV_LINKS.map(link => {
            // Check if this specific main link is the active page
            const isActive = link.link && currentPath.includes(link.label.toLowerCase());

            return (
              <div key={link.label} style={{ position: "relative" }}
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.link ? (
                  <a href={link.link} style={{
                    background: isActive ? "#EFF6FF" : "none",
                    border: isActive ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: isActive ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s, background .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8, textDecoration: "none"
                  }}>
                    {link.label}
                  </a>
                ) : (
                  <button style={{
                    // Highlight the "Services" button if we are on the web-development page
                    background: (link.label === "Services" && currentPath === "/web-development") ? "#EFF6FF" : "none",
                    border: (link.label === "Services" && currentPath === "/web-development") ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: (link.label === "Services" && currentPath === "/web-development") ? "#3B82F6" : activeDropdown === link.label ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8,
                  }}>
                    {link.label}
                    {link.dropdown && <span style={{ fontSize: 10, transition: "transform .2s", transform: activeDropdown === link.label ? "rotate(180deg)" : "rotate(0)" }}>▾</span>}
                  </button>
                )}
                {link.dropdown && activeDropdown === link.label && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, zIndex: 200,
                    background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
                    boxShadow: "0 16px 48px rgba(0,0,0,0.12)", minWidth: 220,
                    padding: "8px 0", animation: "fadeIn .2s ease",
                  }}>
                    {link.dropdown.map(item => (
                      <a key={item.name} href={item.link} style={{
                        display: "block", padding: "9px 20px", fontSize: 13.5,
                        // Highlight the active item inside the dropdown
                        color: currentPath === item.link ? "#3B82F6" : "#374151",
                        background: currentPath === item.link ? "#EFF6FF" : "none",
                        fontWeight: currentPath === item.link ? 700 : 500,
                        textDecoration: "none", transition: "all .15s",
                      }}
                        onMouseEnter={e => { e.target.style.background = "#EFF6FF"; e.target.style.color = "#3B82F6"; }}
                        onMouseLeave={e => { 
                          e.target.style.background = currentPath === item.link ? "#EFF6FF" : "none"; 
                          e.target.style.color = currentPath === item.link ? "#3B82F6" : "#374151"; 
                        }}
                      >{item.name}</a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="/#contact" className="nav-cta" style={{
            background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff",
            padding: "10px 22px", borderRadius: 8, fontSize: 13.5, fontWeight: 700,
            textDecoration: "none", boxShadow: "0 4px 14px rgba(59,130,246,.35)",
            transition: "transform .2s, box-shadow .2s", fontFamily: "'Sora',sans-serif",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(59,130,246,.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(59,130,246,.35)"; }}
          >Get Free Consultation</a>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-menu-btn" style={{
            background: "none", border: "1px solid #E5E7EB", borderRadius: 8,
            padding: "8px 12px", cursor: "pointer", fontSize: 18, color: "#374151",
          }}>☰</button>
        </div>
      </div>
      {mobileOpen && (
        <div style={{ background: "#fff", borderTop: "1px solid #E5E7EB", padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ paddingBottom: 14, borderBottom: "1px solid #F3F4F6", marginBottom: 4 }}>
            <img src={LOGO_SRC} alt="Techvanto" style={{ height: 40, width: "auto", objectFit: "contain" }} />
          </div>
          {NAV_LINKS.map(l => (
            <a key={l.label} href={l.link || "/#services"} style={{ padding: "10px 0", color: currentPath.includes(l.label.toLowerCase()) ? "#3B82F6" : "#374151", fontWeight: 600, textDecoration: "none", borderBottom: "1px solid #F3F4F6", fontFamily: "'Sora',sans-serif" }}>{l.label}</a>
          ))}
          <a href="/#contact" style={{ marginTop: 12, background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff", padding: "13px", borderRadius: 8, textAlign: "center", fontWeight: 700, textDecoration: "none" }}>Get Free Consultation</a>
        </div>
      )}
    </nav>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────

function Footer() {
  const cols = [
    { title: "Services", links: ["Web Development", "Mobile Apps", "AI & ML", "Cloud & DevOps", "UI/UX Design", "E-Commerce"] },
    { title: "Company", links: ["About Us", "Portfolio", "Blog", "Careers", "Contact", "Privacy Policy"] },
    { title: "Technologies", links: ["React & Next.js", "Flutter & RN", "Node.js & Laravel", "AWS & GCP", "Docker & K8s", "Python & AI"] },
  ];
  return (
    <footer style={{ background: "#0F172A", color: "#fff", padding: "72px 40px 32px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div className="footer-grid" style={{ marginBottom: 56 }}>
          <div>
            <div style={{ marginBottom: 16 }}>
              <img src={LOGO_SRC} alt="Techvanto" style={{ height: 140, width: "auto", objectFit: "contain", filter: "brightness(0) invert(1)", display: "block" }} />
            </div>
            <p style={{ color: "#94A3B8", lineHeight: 1.75, fontSize: 14, maxWidth: 300, marginBottom: 24 }}>
              Building world-class digital products for businesses across 20+ countries. Innovation. Scale. Excellence.
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              {["𝕏", "in", "𝔾", "▶"].map((icon, i) => (
                <a key={i} href="#" style={{
                  width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.08)",
                  display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", textDecoration: "none", fontSize: 14, fontWeight: 700, transition: "all .2s",
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#3B82F6"; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.08)"; e.currentTarget.style.color = "#94A3B8"; }}
                >{icon}</a>
              ))}
            </div>
          </div>
          {cols.map(col => (
            <div key={col.title}>
              <h4 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#fff", marginBottom: 20, letterSpacing: ".04em" }}>{col.title}</h4>
              {col.links.map(l => (
                <a key={l} href="#" style={{ display: "block", color: "#64748B", fontSize: 13.5, textDecoration: "none", marginBottom: 10, transition: "color .15s" }}
                  onMouseEnter={e => e.target.style.color = "#93C5FD"}
                  onMouseLeave={e => e.target.style.color = "#64748B"}
                >{l}</a>
              ))}
            </div>
          ))}
        </div>
        <div className="footer-bottom" style={{ borderTop: "1px solid #1E293B", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <p style={{ color: "#475569", fontSize: 13 }}>© 2025 Techvanto Private Limited. All rights reserved.</p>
          <p style={{ color: "#475569", fontSize: 13 }}>Made with ❤️ in India 🇮🇳</p>
        </div>
      </div>
    </footer>
  );
}

// ── Main All Blogs Page Component ─────────────────────────────────────────────

export default function AllBlogs() {
  const [activeCategory, setActiveCategory] = useState("All");
  
  // Extract unique categories for filter
  const categories = ["All", ...new Set(ALL_BLOG_POSTS.map(p => p.category))];

  // Filter posts based on active category
  const filteredPosts = activeCategory === "All" 
    ? ALL_BLOG_POSTS 
    : ALL_BLOG_POSTS.filter(p => p.category === activeCategory);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'Sora', -apple-system, sans-serif; background: #FAFAFA; color: #0F172A; overflow-x: hidden; }

        @keyframes fadeIn { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:none; } }

        .nav-links     { display:flex; align-items:center; gap:4px; }
        .nav-cta       { display:flex !important; }
        .mob-menu-btn  { display:none; }
        .footer-grid   { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:48px; }
        .blog-grid     { display:grid; grid-template-columns:repeat(auto-fill, minmax(320px, 1fr)); gap: 32px; }

        @media (max-width: 1024px) {
          .footer-grid { grid-template-columns: 1fr 1fr; gap: 32px; }
        }
        @media (max-width: 900px) {
          .nav-links    { display:none !important; }
          .nav-cta      { display:none !important; }
          .mob-menu-btn { display:flex !important; }
          .hero-title   { font-size: 38px !important; }
        }
        @media (max-width: 640px) {
          .footer-grid  { grid-template-columns: 1fr; }
          .blog-grid    { grid-template-columns: 1fr; }
        }
      `}</style>


      <main style={{ paddingTop: 140, paddingBottom: 96, maxWidth: 1280, margin: "0 auto", paddingLeft: 40, paddingRight: 40 }}>
        
        {/* Header Section */}
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <Badge color="#F59E0B">Insights & Resources</Badge>
          <h1 className="hero-title" style={{ fontFamily: "'Sora', sans-serif", fontSize: "clamp(36px, 5vw, 52px)", fontWeight: 900, color: "#0F172A", margin: "16px 0 20px" }}>
            The Techvanto Blog
          </h1>
          <p style={{ fontSize: 17, color: "#6B7280", maxWidth: 600, margin: "0 auto", lineHeight: 1.7 }}>
            Discover the latest trends, engineering tutorials, and strategic insights from our team of industry experts.
          </p>
        </div>

        {/* Filter Categories */}
        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 48 }}>
          {categories.map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)} style={{
              padding: "9px 22px", borderRadius: 99, fontSize: 13.5, fontWeight: 700,
              cursor: "pointer", transition: "all .2s", fontFamily: "'Sora',sans-serif",
              background: activeCategory === cat ? "linear-gradient(135deg,#1E3A8A,#3B82F6)" : "#fff",
              color: activeCategory === cat ? "#fff" : "#374151",
              border: activeCategory === cat ? "none" : "1.5px solid #E5E7EB",
              boxShadow: activeCategory === cat ? "0 4px 14px rgba(59,130,246,.35)" : "none",
            }}>
              {cat}
            </button>
          ))}
        </div>

        {/* Blog Post Grid */}
        <div className="blog-grid">
          {filteredPosts.map((post, i) => (
            <article key={post.title} style={{
              background: "#fff", borderRadius: 20, overflow: "hidden", border: "1px solid #E5E7EB",
              transition: "all .3s ease", cursor: "pointer",
              animation: `fadeIn .5s ease ${i * .1}s both`,
              boxShadow: "0 4px 12px rgba(0,0,0,0.03)"
            }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 16px 48px rgba(0,0,0,.08)"; e.currentTarget.style.transform = "translateY(-6px)"; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.03)"; e.currentTarget.style.transform = "none"; }}
            >
              <a href={post.link} style={{ textDecoration: 'none' }}>
                <div style={{ position: "relative", overflow: "hidden" }}>
                  <img src={post.img} alt={post.title} style={{ width: "100%", height: 220, objectFit: "cover", transition: "transform .4s" }}
                    onMouseEnter={e => e.target.style.transform = "scale(1.05)"}
                    onMouseLeave={e => e.target.style.transform = "none"}
                  />
                  <div style={{ position: "absolute", top: 14, left: 14 }}>
                    <Badge color="#3B82F6">{post.category}</Badge>
                  </div>
                </div>
              </a>
              
              <div style={{ padding: "28px 24px" }}>
                <div style={{ display: "flex", gap: 12, marginBottom: 12, fontSize: 12, color: "#9CA3AF" }}>
                  <span>📅 {post.date}</span>
                  <span>⏱ {post.readTime} read</span>
                </div>
                
                <a href={post.link} style={{ textDecoration: 'none' }}>
                  <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, color: "#0F172A", lineHeight: 1.4, marginBottom: 12 }}>
                    {post.title}
                  </h3>
                </a>
                
                <p style={{ fontSize: 14, color: "#6B7280", lineHeight: 1.65, marginBottom: 20 }}>
                  {post.excerpt}
                </p>
                
                <div style={{ borderTop: "1px solid #F1F5F9", paddingTop: 16 }}>
                  <a href={post.link} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#3B82F6", fontWeight: 700, fontSize: 13.5, textDecoration: "none", transition: "gap .2s" }}
                     onMouseEnter={e => e.currentTarget.style.gap = "10px"}
                     onMouseLeave={e => e.currentTarget.style.gap = "6px"}
                  >
                    Read full article →
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Load More Button */}
        {filteredPosts.length > 0 && (
          <div style={{ textAlign: "center", marginTop: 56 }}>
            <button style={{
              background: "#fff", color: "#374151", border: "2px solid #E5E7EB",
              padding: "14px 36px", borderRadius: 10, fontWeight: 700, fontSize: 15,
              cursor: "pointer", fontFamily: "'Sora',sans-serif", transition: "all .2s",
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#93C5FD"; e.currentTarget.style.color = "#1E3A8A"; e.currentTarget.style.background = "#EFF6FF"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.color = "#374151"; e.currentTarget.style.background = "#fff"; }}
            >Load More Articles ↓</button>
          </div>
        )}

      </main>

    </>
  );
}